import { KeyboardEvent, MouseEvent, useState } from "react";
import { Divider } from "@mui/material";
import { dateFormat } from "utils/date";
import {
	FlyoutConstructionLabel,
	FlyoutDoorStyleLabel,
	FlyoutFinishNameLabel,
	FlyoutItemNumberLabel,
	FlyoutModificationsLabel,
	FlyoutStandardDimensionsLabel,
	HingeText,
	ListPriceText,
	OrderDetailFlyoutAlertHeaderCabinet,
	OrderDetailFlyoutAlertHeaderParts,
	OrderDetailFlyoutArrivalDateLabel,
	OrderDetailFlyoutBackorderCabinetAlertTextPartOne,
	OrderDetailFlyoutBackorderCabinetAlertTextPartTwo,
	OrderDetailFlyoutBackorderPartsAlertText,
	OrderDetailFlyoutDepthLabel,
	OrderDetailFlyoutExtendedPriceLabel,
	OrderDetailFlyoutHeader,
	OrderDetailFlyoutHeightLabel,
	OrderDetailFlyoutModificationsPriceLabel,
	OrderDetailFlyoutModificationsValueLabel,
	OrderDetailFlyoutQualityBackorderedLabel,
	OrderDetailFlyoutQuantityLabel,
	OrderDetailFlyoutWidthLabel,
	QuantityText,
	UnavailableDataPlaceholderText
} from "constants/text";
import CloseIcon from "@mui/icons-material/Close";
import { toFormattedPrice } from "utils/string";
import { brandCheck } from "utils/order";
import StyledSummaryCardLogo from "styles/StyledComponents/StyledSummaryCardLogo";
import {
	ItemFlyoutDetailsContainer,
	ItemsOnBackorderButton,
	OrderDetailsFlyoutAlert,
	OrderDetailsFlyoutContainer,
	OrderDetailsFlyoutContainerAlertPartsContainer,
	OrderDetailsFlyoutContainerAlertPartsGrid,
	OrderDetailsFlyoutContent,
	OrderDetailsFlyoutFooter,
	OrderDetailsFlyoutHeader,
	OrderDetailsFlyoutItemDetails,
	OrderDetailsFlyoutResultBackground,
	OrderDetailsFlyoutShadowBlur,
	SkuDescription
} from "./OrderDetailsLineItemFlyout.styles";
import { models } from "types/api/viewModels.ts";
import { formatFinishDescription } from "../NewOrders/utils/NewOrderUtils.tsx";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { OrderType } from "types/declarations/enums.ts";

interface OrderDetailsLineItemFlyoutProps {
	isCustomColor?: boolean;
	closeDrawer: (event: KeyboardEvent | MouseEvent) => void;
	lineItemInformation: models["LineItemViewModel"] | null | undefined;
	globalInformation: models["GlobalsViewModel"] | null | undefined;
	orderDetail: models["OrderDetailViewModel"];
}

const OrderDetailsLineItemFlyout = ({
	isCustomColor,
	closeDrawer,
	lineItemInformation,
	globalInformation,
	orderDetail
}: OrderDetailsLineItemFlyoutProps) => {
	const [toggleBackorderGrid, setToggleBackorderGrid] = useState(false);
	const togglePartsGrid = () => {
		setToggleBackorderGrid((prev) => !prev);
	};

	const modifications = lineItemInformation?.modifications ?? [];
	const inches = lineItemInformation?.measurements?.units?.toUpperCase() === "IN";

	const measurementsValue = (measurement: number | undefined) => {
		if (measurement === 0 || !measurement) {
			return UnavailableDataPlaceholderText;
		} else {
			return measurement?.toString().replace(/\.00$/, "");
		}
	};

	return (
		<OrderDetailsFlyoutContainer data-testid="order-details-line-item-flyout-container">
			<OrderDetailsFlyoutHeader>
				<h5 data-testid="order-details-flyout-header">{OrderDetailFlyoutHeader}</h5>
				<CloseIcon onClick={closeDrawer} />
			</OrderDetailsFlyoutHeader>

			<OrderDetailsFlyoutContent>
				{(lineItemInformation?.partInfo?.partsQuantityBackordered ||
					lineItemInformation?.quantityBackordered !== 0) &&
				orderDetail.orderType !== OrderType.BACKORDER ? (
					<OrderDetailsFlyoutShadowBlur>
						<OrderDetailsFlyoutAlert>
							<img
								src="/assets/BackorderIcon.svg"
								alt="Order Details Line Item Backorder Logo"
								data-testid="order-detail-line-items-flyout-backorder-logo"
							/>
							<h6 data-testid="order-details-line-item-flyout-backorder-header">
								{lineItemInformation?.quantityBackordered !== 0
									? OrderDetailFlyoutAlertHeaderCabinet
									: OrderDetailFlyoutAlertHeaderParts}
							</h6>
							<span data-testid="order-details-line-item-flyout-backorder-description">
								{lineItemInformation?.quantityBackordered !== 0
									? `${OrderDetailFlyoutBackorderCabinetAlertTextPartOne} (${lineItemInformation?.description}) ${OrderDetailFlyoutBackorderCabinetAlertTextPartTwo}`
									: OrderDetailFlyoutBackorderPartsAlertText}
							</span>
							{lineItemInformation?.quantityBackordered !== 0 ? (
								<div data-testid="order-details-line-item-flyout-backorder-details">
									{lineItemInformation?.quantityBackordered && (
										<span>
											{OrderDetailFlyoutQualityBackorderedLabel}:{" "}
											{lineItemInformation?.quantityBackordered}
										</span>
									)}
									{orderDetail.arrivalDate && (
										<span>
											{OrderDetailFlyoutArrivalDateLabel}: {dateFormat(orderDetail.arrivalDate)}
										</span>
									)}
								</div>
							) : (
								<ItemsOnBackorderButton
									variant="outlined"
									data-testid="order-details-line-item-flyout-backorder-parts-button"
									onClick={() => togglePartsGrid()}
								>
									{OrderDetailFlyoutAlertHeaderParts.toUpperCase()} (
									{lineItemInformation?.partInfo?.parts?.map((part) => part).length})
									{toggleBackorderGrid ? <RemoveIcon /> : <AddIcon />}
								</ItemsOnBackorderButton>
							)}
						</OrderDetailsFlyoutAlert>
						{lineItemInformation?.quantityBackordered === 0 && (
							<OrderDetailsFlyoutResultBackground>
								{toggleBackorderGrid && (
									<OrderDetailsFlyoutContainerAlertPartsContainer>
										{lineItemInformation?.partInfo?.parts?.map((part) => (
											<>
												<OrderDetailsFlyoutContainerAlertPartsGrid key={part.number}>
													<div>
														<span>Part</span>
														<span>
															{part.description ?? UnavailableDataPlaceholderText}
														</span>
													</div>
													<div>
														<span>Part #</span>
														<span>{part.number ?? UnavailableDataPlaceholderText}</span>
													</div>
													<div>
														<span>{QuantityText}</span>
														<span>{part.quantity ?? UnavailableDataPlaceholderText}</span>
													</div>
												</OrderDetailsFlyoutContainerAlertPartsGrid>
												<Divider />
											</>
										))}
									</OrderDetailsFlyoutContainerAlertPartsContainer>
								)}
							</OrderDetailsFlyoutResultBackground>
						)}
					</OrderDetailsFlyoutShadowBlur>
				) : undefined}

				<OrderDetailsFlyoutItemDetails>
					<SkuDescription>
						<h6 data-testid="order-detail-line-items-flyout-sku">{lineItemInformation?.sku}</h6>
						<span data-testid="order-detail-line-items-flyout-description">
							{lineItemInformation?.description}
						</span>
					</SkuDescription>
					<ItemFlyoutDetailsContainer>
						<StyledSummaryCardLogo
							data-testid="order-line-items-flyout-logo"
							parentBrand={globalInformation?.brand?.code}
							src={
								brandCheck(globalInformation?.brand?.code)
									? `/assets/manufacture_logos/${globalInformation?.brand?.code}.png`
									: "/assets/tandem_logos/cwg_logo.png"
							}
							alt={
								brandCheck(globalInformation?.brand?.code)
									? String(globalInformation?.brand?.code)
									: "CabinetworksGroup Logo"
							}
						/>
						<div>
							<span>{FlyoutItemNumberLabel}</span>
							<span data-testid="order-details-line-item-flyout-item-number">
								{lineItemInformation?.name ?? UnavailableDataPlaceholderText}
							</span>
						</div>
						<div>
							<span>{FlyoutDoorStyleLabel}</span>
							<span data-testid="order-details-line-item-flyout-door-style">
								{lineItemInformation?.doorStyle ?? UnavailableDataPlaceholderText}
							</span>
						</div>
						<div>
							<span>{FlyoutConstructionLabel}</span>
							<span data-testid="order-details-line-item-flyout-construction">
								{lineItemInformation?.construction ?? UnavailableDataPlaceholderText}
							</span>
						</div>
						<div>
							<span>{HingeText}</span>
							<span data-testid="order-details-line-item-flyout-hinge">
								{lineItemInformation?.hinge ?? UnavailableDataPlaceholderText}
							</span>
						</div>
						<div>
							<span>{FlyoutFinishNameLabel}</span>
							<span data-testid="order-details-line-item-flyout-finish">
								{formatFinishDescription(globalInformation?.finish, Boolean(isCustomColor))}
							</span>
						</div>
					</ItemFlyoutDetailsContainer>
					<ItemFlyoutDetailsContainer>
						<label>{FlyoutStandardDimensionsLabel}</label>
						<div>
							<span>{OrderDetailFlyoutWidthLabel}</span>
							<span data-testid="order-details-line-item-flyout-width">
								{measurementsValue(lineItemInformation?.measurements?.width)}
								{lineItemInformation?.measurements?.width !== 0 && inches && " inches"}
							</span>
						</div>
						<div>
							<span>{OrderDetailFlyoutDepthLabel}</span>
							<span data-testid="order-details-line-item-flyout-depth">
								{measurementsValue(lineItemInformation?.measurements?.depth)}
								{lineItemInformation?.measurements?.depth !== 0 && inches && " inches"}
							</span>
						</div>
						<div>
							<span>{OrderDetailFlyoutHeightLabel}</span>
							<span data-testid="order-details-line-item-flyout-height">
								{measurementsValue(lineItemInformation?.measurements?.height)}
								{lineItemInformation?.measurements?.height !== 0 && inches && " inches"}
							</span>
						</div>
					</ItemFlyoutDetailsContainer>
					<ItemFlyoutDetailsContainer>
						<label>{FlyoutModificationsLabel}</label>
						{modifications.map((modification) => (
							<>
								<div>
									<span>{modification.sku}</span>
									<span data-testid="order-details-line-item-flyout-modification-details">
										{modification.description ?? UnavailableDataPlaceholderText}
									</span>
								</div>
								<div>
									<span>{OrderDetailFlyoutModificationsPriceLabel}</span>
									<span data-testid="order-details-line-item-flyout-modification-price">
										{toFormattedPrice(modification.netPrice ?? modification.listPrice)}
									</span>
								</div>
								{modifications.values && modifications.values.length > 0 && (
									<div>
										<span>{OrderDetailFlyoutModificationsValueLabel}</span>
										{modification?.values?.map((value) => (
											<span
												key={value.value}
												data-testid="order-details-line-item-flyout-modification-value"
											>
												{value.value ?? UnavailableDataPlaceholderText}
											</span>
										))}
									</div>
								)}
							</>
						))}
						{modifications?.length === 0 && <span>{UnavailableDataPlaceholderText}</span>}
					</ItemFlyoutDetailsContainer>
					<ItemFlyoutDetailsContainer>
						<label>{OrderDetailFlyoutQuantityLabel}</label>
						<div>
							<span data-testid="order-details-line-item-flyout-quantity-ordered">
								{lineItemInformation?.quantityOrdered ?? UnavailableDataPlaceholderText}
							</span>
						</div>
					</ItemFlyoutDetailsContainer>
				</OrderDetailsFlyoutItemDetails>
			</OrderDetailsFlyoutContent>

			<OrderDetailsFlyoutFooter>
				<div>
					<span>{ListPriceText}</span>
					<span data-testid="order-details-line-item-flyout-list-price">
						{toFormattedPrice(
							lineItemInformation?.pricing?.adjustedNetPrice ??
								lineItemInformation?.pricing?.adjustedListPrice
						)}
					</span>
				</div>
				<div>
					<span>{OrderDetailFlyoutExtendedPriceLabel}</span>
					<span data-testid="order-details-line-item-flyout-extended-price">
						{toFormattedPrice(
							lineItemInformation?.pricing?.extendedAdjustedNetPrice ??
								lineItemInformation?.pricing?.extendedAdjustedListPrice
						)}
					</span>
				</div>
			</OrderDetailsFlyoutFooter>
		</OrderDetailsFlyoutContainer>
	);
};

export default OrderDetailsLineItemFlyout;
